// Accessible "ⓘ" info tooltip/popover for SACs.
// - Desktop: opens on hover AND keyboard focus; closes on leave / blur / Escape.
// - Mobile/touch: opens on tap (toggle); closes on outside tap / Escape.
// - Fixed-positioned so card overflow never clips it; flips above / clamps
//   horizontally to stay on-screen. Smooth fade-in. Fully additive.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { SacInfo, SacKey } from '@/content/sacInfo';
import { SAC_INFO } from '@/content/sacInfo';
import { fontPx } from '@/lib/fontSize';

interface InfoTooltipProps {
  /** A registry key, or an inline SacInfo object. */
  info: SacKey | SacInfo;
  /** Accessible label for the trigger button. */
  label?: string;
}

const WIDTH = 288;
const GAP = 8;

export function InfoTooltip({ info, label }: InfoTooltipProps) {
  const data: SacInfo = typeof info === 'string' ? SAC_INFO[info] : info;
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean }>({ top: 0, left: 0, above: false });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const openedByHover = useRef(false);

  const place = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const popH = popRef.current?.offsetHeight ?? 220;
    const above = b.bottom + GAP + popH > vh && b.top - GAP - popH > 0;
    let left = b.left;
    if (left + WIDTH > vw - GAP) left = Math.max(GAP, vw - GAP - WIDTH);
    const top = above ? b.top - GAP - popH : b.bottom + GAP;
    setPos({ top, left, above });
  }, []);

  // Show → place → fade in. Recompute on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open) return;
    place();
    const t = window.setTimeout(() => setVisible(true), 10);
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  // Close on Escape + outside click (touch).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !popRef.current?.contains(t)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
     
  }, [open]);

  function show(byHover: boolean) {
    openedByHover.current = byHover;
    setOpen(true);
  }
  function close() {
    setVisible(false);
    setOpen(false);
    openedByHover.current = false;
  }

  const section = (heading: string, text: string) => (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: fontPx(10.5), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-hint)' }}>{heading}</div>
      <div style={{ fontSize: fontPx(12.5), lineHeight: 1.45, color: 'var(--text-body)', marginTop: 1 }}>{text}</div>
    </div>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label ?? `About ${data.name}`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => show(true)}
        onMouseLeave={() => { if (openedByHover.current) close(); }}
        onFocus={() => show(false)}
        onBlur={() => { if (!openedByHover.current) close(); }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (open) close();
          else show(false);
        }}
        style={{
          display: 'inline-grid', placeItems: 'center', width: 16, height: 16, marginLeft: 2,
          borderRadius: '50%', border: '1px solid var(--border-default)', background: 'transparent',
          color: 'var(--text-hint)', fontSize: fontPx(11), fontWeight: 700, lineHeight: 1, cursor: 'pointer', flex: '0 0 auto',
        }}
      >
        i
      </button>

      {open && (
        <div
          ref={popRef}
          id={id}
          role="tooltip"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: WIDTH, zIndex: 60,
            background: 'var(--surface-card, #fff)', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md, 10px)', padding: '12px 14px',
            boxShadow: '0 8px 28px rgba(20,40,15,.16)',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : `translateY(${pos.above ? 4 : -4}px)`,
            transition: 'opacity 150ms ease, transform 150ms ease',
            pointerEvents: 'auto',
          }}
        >
          <div style={{ fontSize: fontPx(13.5), fontWeight: 700, color: 'var(--text-accent)' }}>{data.name}</div>
          {section('What it measures', data.measures)}
          {section('Why it matters', data.matters)}
          {section('How to improve it', data.improve)}
          {section('A good sign', data.goodScore)}
        </div>
      )}
    </>
  );
}
